import React, { useState, useEffect } from 'react';
import ApiService from '../../services/apiService';
import './LibraryGalleryManager.css';

const LibraryGalleryManager = () => {
  const [galleryImages, setGalleryImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    alt_text: '',
    display_order: '',
    image: null
  });

  useEffect(() => {
    loadGalleryImages();
  }, []);

  const loadGalleryImages = async () => {
    try {
      setLoading(true);
      const response = await ApiService.getAllLibraryGalleryImages();
      if (response.success) {
        setGalleryImages(response.data);
      } else {
        setError('Failed to load library gallery images');
      }
    } catch (error) {
      console.error('Error loading library gallery images:', error);
      setError('Error loading library gallery images');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      
      const submitData = new FormData();
      Object.keys(formData).forEach(key => {
        if (formData[key] !== null && formData[key] !== '') {
          submitData.append(key, formData[key]);
        }
      });

      const response = await ApiService.createLibraryGalleryImage(Object.fromEntries(submitData));

      if (response.success) {
        await loadGalleryImages();
        resetForm();
        setError('');
      } else {
        setError(response.message || 'Failed to save library gallery image');
      }
    } catch (error) {
      console.error('Error saving library gallery image:', error);
      setError('Error saving library gallery image');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this library gallery image?')) {
      return;
    }

    try {
      setLoading(true);
      const response = await ApiService.deleteLibraryGalleryImage(id);
      if (response.success) {
        await loadGalleryImages();
        setError('');
      } else {
        setError('Failed to delete library gallery image');
      }
    } catch (error) {
      console.error('Error deleting library gallery image:', error);
      setError('Error deleting library gallery image');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      alt_text: '',
      display_order: '',
      image: null
    });
    setShowForm(false);
  };

  const handleInputChange = (e) => {
    const { name, value, type, files } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'file' ? files[0] : value
    }));
  };

  if (loading && galleryImages.length === 0) {
    return <div className="library-gallery-loading">Loading library gallery images...</div>;
  }

  return (
    <div className="library-gallery-manager">
      <div className="library-gallery-manager-header">
        <h2>Library Gallery Management</h2>
        <button 
          className="library-gallery-add-btn" 
          onClick={() => setShowForm(!showForm)}
        >
          {showForm ? 'Cancel' : 'Add New Image'}
        </button>
      </div>

      {error && <div className="library-gallery-error-message">{error}</div>}

      {showForm && (
        <div className="library-gallery-form-container">
          <h3>Add New Library Gallery Image</h3>
          <form onSubmit={handleSubmit} className="library-gallery-form">
            <div className="library-gallery-form-group">
              <label htmlFor="title">Title *</label>
              <input
                type="text"
                id="title"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                required
                placeholder="Enter image title"
              />
            </div>

            <div className="library-gallery-form-group">
              <label htmlFor="description">Description</label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                placeholder="Enter image description"
                rows="3"
              />
            </div>

            <div className="library-gallery-form-group">
              <label htmlFor="alt_text">Alt Text</label>
              <input
                type="text"
                id="alt_text"
                name="alt_text"
                value={formData.alt_text}
                onChange={handleInputChange}
                placeholder="Enter alt text for accessibility"
              />
            </div>

            <div className="library-gallery-form-group">
              <label htmlFor="display_order">Display Order</label>
              <input
                type="number"
                id="display_order"
                name="display_order"
                value={formData.display_order}
                onChange={handleInputChange}
                min="0"
                placeholder="Display order (leave empty for auto)"
              />
            </div>

            <div className="library-gallery-form-group">
              <label htmlFor="image">Image *</label>
              <input
                type="file"
                id="image"
                name="image"
                onChange={handleInputChange}
                accept="image/*"
                required
              />
            </div>

            <div className="library-gallery-form-actions">
              <button 
                type="submit" 
                className="library-gallery-btn library-gallery-btn-primary"
                disabled={loading}
              >
                {loading ? 'Saving...' : 'Add Image'}
              </button>
              <button 
                type="button" 
                className="library-gallery-btn library-gallery-btn-secondary"
                onClick={resetForm}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="library-gallery-images-list">
        <h3>Current Library Gallery Images ({galleryImages.length})</h3>
        {galleryImages.length === 0 ? (
          <p className="library-gallery-no-images">No library gallery images found. Add some images to get started!</p>
        ) : (
          <div className="library-gallery-images-grid">
            {galleryImages.map((image) => (
              <div key={image.id} className={`library-gallery-image-card ${!image.is_active ? 'inactive' : ''}`}>
                <div className="library-gallery-image-preview">
                  {image.image_url ? (
                    <img src={image.image_url} alt={image.alt_text || image.title} />
                  ) : (
                    <div className="library-gallery-no-image">No Image</div>
                  )}
                </div>
                
                <div className="library-gallery-image-info">
                  <h4>{image.title}</h4>
                  {image.description && (
                    <p className="library-gallery-description">{image.description}</p>
                  )}
                  <div className="library-gallery-image-meta">
                    <span className={`library-gallery-status ${image.is_active ? 'active' : 'inactive'}`}>
                      {image.is_active ? 'Active' : 'Inactive'}
                    </span>
                    <span className="library-gallery-order">Order: {image.display_order}</span>
                  </div>
                </div>

                <div className="library-gallery-image-actions">
                  <button 
                    className="library-gallery-btn library-gallery-btn-danger library-gallery-btn-small"
                    onClick={() => handleDelete(image.id)}
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default LibraryGalleryManager;